import type { LocaleMessages } from "@/i18n/types";
import type { LocaleContent } from "@/types/site";

export const enMessages: LocaleMessages = {
  actions: {
    backToHome: "Back to home",
    copyLink: "Copy link",
    copyLinkSuccess: "Link copied to clipboard",
    openMenu: "Open navigation menu",
    closeMenu: "Close navigation menu",
    share: "Share",
    signIn: "Sign in",
    signingIn: "Signing in…",
    signOut: "Sign out",
    submit: "Send",
    submitting: "Sending…",
    switchLanguage: "Change language",
  },
  auth: {
    loggedInAs: "Signed in as",
    mockOnly: "Local mock only. Do not ship these credentials to production.",
    seededAccounts: "Seeded accounts",
    seedHint: "Redirects to {target} after sign-in.",
    useCredentials: "Use these credentials locally.",
  },
  common: {
    description: "Description",
    email: "Email",
    formErrors: "Please fix the highlighted fields and try again.",
    loading: "Loading…",
    optional: "Optional",
    password: "Password",
    required: "Required",
    skipToContent: "Skip to main content",
    toggleNav: "Toggle navigation",
  },
  consent: {
    accept: "Accept analytics",
    decline: "Decline",
    description:
      "We use privacy-friendly analytics to see which parts of the portfolio people use. No cookies. No cross-site tracking.",
    learnMore: "Read the privacy note",
    title: "Help improve the site",
  },
  dashboard: {
    accountNav: "Account",
    adminNav: "Admin",
    overviewNav: "Overview",
    signedOut: "You have signed out.",
    welcome: "Welcome back",
  },
  errors: {
    boundaryHint: "Try reloading the page. If it keeps happening, reach out directly.",
    boundaryTitle: "Something went wrong while rendering this view.",
    notFoundIntro: "That page is not part of the public portfolio.",
    notFoundTitle: "Page not found.",
    unknownLocale: "Unknown locale. Defaulting to English.",
  },
  share: {
    email: "Share via email",
    linkedin: "Share on LinkedIn",
    native: "Share…",
    twitter: "Share on X",
  },
  sitemap: {
    jumpToSection: "Jump to section",
  },
};

export const siteContentEn: LocaleContent = {
  contact: {
    description:
      "Email is the best way to reach me. I am open to software engineering opportunities, product work, and conversations with people building useful things.",
    methods: [
      {
        href: "mailto:i.ramosvillafane@gmail.com",
        label: "Email",
        showValue: false,
        value: "i.ramosvillafane@gmail.com",
      },
      {
        href: "tel:+18027331254",
        label: "Phone",
        showValue: false,
        value: "(802) 733-1254",
      },
      {
        external: true,
        href: "https://github.com/igrv2003",
        label: "GitHub",
        showValue: false,
        value: "github.com/igrv2003",
      },
      {
        external: true,
        href: "https://www.linkedin.com/in/ian-g-ramos/",
        label: "LinkedIn",
        showValue: false,
        value: "linkedin.com/in/ian-g-ramos",
      },
    ],
    resume: {
      download: true,
      href: "/resume/ian-gabriel-ramos-villafane-resume.pdf",
      label: "Download resume",
    },
    title: "Contact",
  },
  contactDetails: {
    availability:
      "Open to software engineering internships, new grad roles, and technical product work.",
    email: "i.ramosvillafane@gmail.com",
    responseWindow: "I usually respond within two business days.",
  },
  experience: {
    items: [
      {
        company: "YouTube",
        icon: "/icons/youtube-icon.svg",
        contributions: [
          "Built a reusable UI component for YouTube Search so people could refine and filter a query without leaving the feed.",
          "Worked from existing YouTube homepage components instead of creating a one-off surface from scratch.",
          "Worked with the CatNorvi team to connect categorical query data to exploratory search flows.",
        ],
        demonstrates:
          "I can work inside a large product surface, coordinate with another team, and keep frontend work reusable instead of one-off.",
        impact:
          "Added mid-feed query refinement and filtering to YouTube Search.",
        location: "San Bruno, CA",
        period: "May 2025 – Aug 2025",
        role: "Software Engineering Intern",
        summary:
          "Worked on YouTube Search, building a reusable frontend component for mid-feed query refinement, filtering, and exploratory browsing.",
        team: "YouTube Search",
        technologies: [
          "Frontend engineering",
          "UI component integration",
          "Query refinement",
          "Search experimentation",
          "Cross-team collaboration",
        ],
      },
      {
        company: "Google Cloud",
        icon: "/icons/google-cloud-icon.svg",
        contributions: [
          "Integrated FedRAMP-aligned work into Google Distributed Cloud Air-Gapped for deployments in isolated environments.",
          "Built TLS-encrypted DNS support for Kubernetes clusters.",
          "Extended the Cluster DNS API and CoreDNS templating path to support encrypted communication.",
        ],
        demonstrates:
          "I can work on infrastructure where security, reliability, and constraints matter from the start.",
        impact:
          "Worked on secure DNS and encrypted communication for Google Distributed Cloud Air-Gapped.",
        location: "Sunnyvale, CA",
        period: "May 2024 – Aug 2024",
        role: "Software Engineering Intern",
        summary:
          "Contributed to Google Cloud infrastructure for restricted environments, with work across DNS security, Kubernetes, CoreDNS, TLS, and FedRAMP-aligned requirements.",
        team: "Google Distributed Cloud Air-Gapped",
        technologies: [
          "Google Cloud",
          "Kubernetes",
          "CoreDNS",
          "TLS",
          "FedRAMP",
          "Cloud infrastructure",
        ],
      },
      {
        company: "YouTube",
        icon: "/icons/youtube-icon.svg",
        contributions: [
          "Built a YouTube Studio search interface with TypeScript and HTML for Primetime partner workflows.",
          'Added a "Video Type" filter in the Content section for Subscription Video on Demand content.',
          "Took the feature through design, implementation, code review, Bug Bash coordination, and internal launch.",
        ],
        demonstrates:
          "I can own a feature past the happy path: requirements, implementation, review, testing, and launch details.",
        impact:
          "Launched a specialized YouTube Studio search interface for Primetime partner content.",
        location: "Alexandria, VA",
        period: "May 2023 – Aug 2023",
        role: "Software Engineering Intern",
        summary:
          "Built and launched a search interface in YouTube Studio that helped Primetime partners filter and manage Subscription Video on Demand content.",
        team: "YouTube Studio",
        technologies: [
          "TypeScript",
          "HTML",
          "Frontend development",
          "Search interfaces",
          "Content management tools",
          "Internal launch processes",
        ],
      },
    ],
  },
  footerGroups: [
    {
      links: [
        { href: "/experience", label: "Experience" },
        { href: "/projects", label: "Projects" },
        { href: "/contact", label: "Contact" },
      ],
      title: "Work",
    },
    {
      links: [
        { href: "/skills", label: "Skills" },
        { href: "/legal", label: "Legal" },
        { href: "/", label: "Home" },
      ],
      title: "Info",
    },
  ],
  home: {
    closing: {
      action: {
        href: "/contact",
        label: "Get in touch",
      },
      description:
        "If you are building something that needs someone who can think through the product and write the code, I am open to talking.",
      title: "Need someone who can own more than a ticket?",
    },
    hero: {
      actions: [
        { href: "/projects", label: "View projects" },
        { href: "/contact", label: "Get in touch" },
      ],
      description:
        "I am a software engineering student from Puerto Rico building iOS apps, web products, and backend systems. My internships at Google and YouTube matter, but they are not the whole story. My base comes from building things of my own.",
      name: "Ian Gabriel Ramos Villafañe",
      portrait: {
        alt: "Ian Gabriel Ramos Villafañe",
        locationLabel: "Mayagüez, Puerto Rico",
        monogram: "IG",
        note: "Software engineering student building apps, websites, and backend systems.",
        src: "/brand/home-photo.png",
      },
      title: "I build software I can stand behind.",
    },
    proof: {
      items: [
        {
          lines: [
            "A lot of my best learning has come from projects where no one handed me a spec.",
            "In Akii and Apolo Inventions, I have had to decide what should exist, what should be cut, and what is worth shipping.",
          ],
          title: "Building from zero",
        },
        {
          lines: [
            "Side projects expose the parts that do not show up in mockups: auth, caching, routing, deployment, broken states, slow paths, and maintenance.",
            "That is where I learned to care about code that survives after the first demo.",
          ],
          title: "Engineering through the rough parts",
        },
        {
          lines: [
            "Google and YouTube gave me a higher bar for reviews, testing, docs, and launch details.",
            "I treat that as an advantage I bring back into my own work, not as the only reason I can build.",
          ],
          title: "Internships as proof, not identity",
        },
      ],
    },
    timeline: {
      items: [
        {
          description:
            "Built the iOS photo app, shaped the product direction, and worked through auth, profiles, uploads, feeds, and backend flows.",
          title: "Akii · iOS product",
        },
        {
          description:
            "Started a web development business for local businesses, handling client conversations, branding, builds, deployment, and support.",
          title: "Apolo Inventions · Web work",
        },
        {
          description:
            "Worked on production surfaces across YouTube Studio, Google Cloud, and YouTube Search.",
          title: "Google + YouTube · Internships",
        },
      ],
      title: "What I build from",
    },
  },
  legal: {
    sections: [
      {
        body: [
          "This site shares professional background, selected experience, and project summaries for portfolio and recruiting use.",
          "Project descriptions stay public and resume-safe. They do not include confidential implementation details.",
        ],
        title: "Portfolio use",
      },
    ],
    updatedAt: "2026-05-01",
  },
  navigation: [
    { href: "/", label: "Home" },
    { href: "/experience", label: "Experience" },
    { href: "/projects", label: "Projects" },
    { href: "/skills", label: "Skills" },
    { href: "/contact", label: "Contact" },
  ],
  pages: {
    contact: {
      description:
        "Contact Ian Ramos for software engineering opportunities, technical product work, or project conversations.",
      eyebrow: "Contact",
      title: "Let's talk.",
    },
    experience: {
      description:
        "Internship work across YouTube Search, Google Cloud, and YouTube Studio.",
      eyebrow: "Experience",
      title: "Work from product UI to cloud infrastructure.",
    },
    home: {
      description:
        "Portfolio for Ian Gabriel Ramos Villafañe, a software engineering student from Puerto Rico building iOS apps, websites, backend systems, and product ideas of his own.",
      eyebrow: "Home",
      title: "Ian Ramos | Software Engineering Portfolio",
    },
    projects: {
      description:
        "Independent work where I owned the product, the implementation, or the path from idea to deployment.",
      eyebrow: "Projects",
      title: "Projects I have built outside internships.",
    },
    skills: {
      description:
        "Languages, frameworks, platforms, and tools I use across mobile, web, backend, and infrastructure work.",
      eyebrow: "Skills",
      title: "The stack I reach for most.",
    },
  },
  projects: {
    items: [
      {
        links: [
          {
            external: true,
            href: "https://akiiapp.com",
            label: "Link",
          },
        ],
        name: "Akii",
        role: "I defined the product direction, designed the experience, and built the iOS app.",
        summary:
          "I started Akii because most photo apps feel built for scrolling. I wanted the opposite: a slower place for real photos taken by people. I built the iOS app with authentication, profiles, uploads, feed navigation, image handling, social interactions, and Supabase-backed data flows.",
        shortDescription:
          "An iOS photo app for real photos taken by people.",
        status: "Public concept and codebase with ongoing product work.",
        technologies: [
          "Swift",
          "SwiftUI",
          "Supabase",
          "Authentication",
          "Image caching",
          "Feed architecture",
        ],
      },
      {
        name: "Personal Finance App",
        role: "I worked on expense input, spending analysis, and notification-based reminders.",
        summary:
          "This project was about making personal spending easier to track. I worked on the pieces that let users enter expenses, review spending patterns, and get notifications when something needed attention.",
        shortDescription:
          "A web app for tracking expenses and reviewing spending patterns.",
        status: "Project details are still being prepared for public release.",
        technologies: [
          "Web development",
          "Frontend engineering",
          "Data visualization",
          "Notifications",
          "Financial tracking",
        ],
      },
      {
        links: [
          {
            external: true,
            href: "https://apoloinv.com",
            label: "Link",
          },
        ],
        name: "Apolo Inventions",
        role: "I handle the work from client conversations to build, deployment, and support.",
        summary:
          "Apolo Inventions is my web development business for local businesses that need a better online presence than a social media profile. The work is simple on purpose: understand what the business needs, shape the brand, build the site, deploy it, and keep it running.",
        shortDescription:
          "A web development business for local business websites.",
        status: "Active client work and internal platform development.",
        technologies: [
          "Web development",
          "Brand systems",
          "Client communication",
          "Deployment workflows",
          "Cloudflare hosting",
        ],
      },
    ],
  },
  skillsPage: {
    description:
      "A practical list of the tools I use most often across mobile, web, backend, and infrastructure work.",
    groups: [
      {
        items: ["Swift", "SwiftUI", "Xcode", "App Store"],
        title: "iOS",
      },
      {
        items: [
          "TypeScript",
          "JavaScript",
          "React",
          "Vite",
          "Tailwind CSS",
          "HTML",
          "CSS",
          "Markdown",
          "Node.js",
        ],
        title: "Web",
      },
      {
        items: [
          "Supabase",
          "PostgreSQL",
          "REST APIs",
          "Cloudflare",
          "Kubernetes",
          "CoreDNS",
        ],
        title: "Backend and Infrastructure",
      },
      {
        items: [
          "Git",
          "GitHub",
          "Figma",
          "Notion",
          "Codex",
          "Claude",
          "Google Gemini",
        ],
        title: "Workflow and AI",
      },
    ],
    title: "Tools I use to build.",
  },
  site: {
    description:
      "Software engineering portfolio for Ian Gabriel Ramos Villafañe, with independent projects, technical skills, internship experience, and contact information.",
    name: "Ian Ramos",
    tagline:
      "Software engineering student building iOS apps, websites, backend systems, and product ideas.",
  },
};
